import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Modal from '../../Modal';
import { analyticsInterceptorV2 } from '../../../utils/Axios/analyticsInterceptorv2';
import { axiosInstancev2 } from '../../../utils/Axios/axiosInterceptorv2';
import { canSendImpactReport } from '../../../utils/permissionKeys';
import {
  fetchPartnerLogo,
  useReportPartners,
} from '../../../hooks/useReportPartners';
import { reportNumbers, unknownNumbers } from './numberCheck';
import {
  MAX_MESSAGE,
  MAX_RECIPIENTS,
  MAX_SUBJECT,
  emailError,
  parseRecipients,
} from './email';

const AUDIENCES = [
  { value: 'funder', label: 'Funder' },
  { value: 'partner', label: 'Partner' },
  { value: 'board', label: 'Board' },
  { value: 'internal', label: 'Internal' },
];

const narrativeError = (error) => {
  const status = error?.response?.status;
  // A JSON 404 means the scope left the report (e.g. after a rebuild); a
  // plain-text 404 means the endpoint isn't deployed.
  if (status === 404) {
    return error?.response?.data?.error
      ? "This year or cohort isn't in the impact report any more; reload the report."
      : "AI drafting isn't available on the server yet.";
  }
  if (status === 503) return "AI drafting isn't switched on yet.";
  if (status === 422) {
    return "The AI draft didn't pass the number check, so it wasn't used. Try again or write the summary yourself.";
  }
  if (status === 502) {
    return "The AI service didn't respond in time. Try again.";
  }
  // The service runs at most two drafts at once, across all users.
  if (status === 429) {
    return 'Someone else is drafting a summary right now. Try again in a moment.';
  }
  return (
    error?.response?.data?.error || "We couldn't draft a summary. Try again."
  );
};

// Prepare and download the branded PDF: pre-share checks, an optional
// AI-drafted (and always human-reviewed) summary, then the file.
function ImpactPdfDialog({
  open,
  onClose,
  scope,
  scopeLabel,
  level,
  summary,
  issues,
  onBuild,
  reportTitle,
  period,
}) {
  const user = useSelector((state) => state.auth.userDetails) ?? {};
  const reviewer =
    [user.firstname, user.lastname].filter(Boolean).join(' ') || 'PLP staff';
  const myEmail = user.email;
  const permissions = useSelector((state) => state.auth.permissions ?? []);
  const mayEmail = canSendImpactReport(permissions, user.role);

  const [audience, setAudience] = useState('partner');
  const [mode, setMode] = useState('none'); // none | ai | manual
  const [text, setText] = useState('');
  const [points, setPoints] = useState([]);
  const [caveats, setCaveats] = useState([]);
  const [facts, setFacts] = useState([]);
  const [reviewed, setReviewed] = useState(false);
  const [building, setBuilding] = useState(false);
  const [delivery, setDelivery] = useState('download'); // download | email
  const [recipientsText, setRecipientsText] = useState('');
  const { data: partners = [], isError: partnersUnavailable } =
    useReportPartners('active');
  const [partnerId, setPartnerId] = useState('');
  const [commissioned, setCommissioned] = useState(true);
  const [swapped, setSwapped] = useState(false);
  const partner = partners.find((p) => p.id === partnerId) ?? null;
  // The commissioning partner's logo leads (left); "Swap sides" overrides.
  const partnerFirst = commissioned ? !swapped : swapped;

  const defaultSubject = partner
    ? `${partner.name} × Power Learn Project – ${reportTitle}`
    : `${reportTitle} – Power Learn Project`;
  const [subject, setSubject] = useState(defaultSubject);
  const [subjectEdited, setSubjectEdited] = useState(false);
  const [message, setMessage] = useState('');
  const [ccMe, setCcMe] = useState(true);

  // Keep edits while the dialog is closed and reopened; start fresh only
  // when the report level changes. (The server's draft can change when the
  // report is rebuilt, so the edited text lives here, not in a re-fetch.)
  const scopeKey = `${scope.year ?? ''}|${scope.cohortId ?? ''}`;
  useEffect(() => {
    if (!subjectEdited) setSubject(defaultSubject);
  }, [defaultSubject, subjectEdited]);

  const [prefilledFrom, setPrefilledFrom] = useState('');
  useEffect(() => {
    const defaults = (partner?.default_recipients ?? []).join('\n');
    setRecipientsText((current) =>
      !current.trim() || current === prefilledFrom ? defaults : current
    );
    setPrefilledFrom(defaults);
    // only when the chosen partner changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  // Brand details for the PDF; the partner logo is fetched as a blob URL.
  const buildBrand = async () => {
    if (!partner) return null;
    const logoUrl = await fetchPartnerLogo(partner).catch(() => null);
    return {
      partner: {
        name: partner.name,
        brandColour: partner.brand_colour,
        logoUrl,
      },
      partnerFirst,
    };
  };
  const build = async (narrative) => {
    const brand = await buildBrand();
    try {
      return await onBuild(narrative, brand);
    } finally {
      if (brand?.partner.logoUrl) URL.revokeObjectURL(brand.partner.logoUrl);
    }
  };

  useEffect(() => {
    setMode('none');
    setText('');
    setPoints([]);
    setCaveats([]);
    setFacts([]);
    setReviewed(false);
  }, [scopeKey]);

  const allowed = useMemo(() => {
    const set = reportNumbers(level, summary?.years);
    facts.forEach((fact) =>
      (String(fact.value).match(/\d[\d,]*(?:\.\d+)?/g) ?? []).forEach((n) =>
        set.add(Number(n.replace(/,/g, '')))
      )
    );
    return set;
  }, [level, summary, facts]);
  const flagged = useMemo(
    () => unknownNumbers([text, ...points].join(' '), allowed),
    [text, points, allowed]
  );

  const draft = useMutation(
    (regenerate) =>
      analyticsInterceptorV2.post(
        '/impact/narrative',
        {
          audience,
          ...(scope.cohortId ? { cohort_id: scope.cohortId } : {}),
          ...(!scope.cohortId && scope.year
            ? { year: Number(scope.year) }
            : {}),
          ...(regenerate ? { regenerate: true } : {}),
        },
        { timeout: 90 * 1000 }
      ),
    {
      onSuccess: (response) => {
        const data = response.data ?? {};
        setMode('ai');
        setText(data.summary ?? '');
        setPoints((data.key_points ?? []).map((point) => point.text));
        setCaveats(data.caveats ?? []);
        setFacts(data.facts ?? []);
        setReviewed(false);
      },
    }
  );

  const editing = mode !== 'none';
  const needsReview = mode === 'ai';
  const canDownload =
    !building && (!needsReview || reviewed) && (!editing || text.trim());

  const buildNarrative = () => {
    if (!editing || !text.trim()) return null;
    const today = format(new Date(), 'd MMMM yyyy');
    return {
      summary: text.trim(),
      keyPoints: points.map((p) => p.trim()).filter(Boolean),
      caveats: mode === 'ai' ? caveats : [],
      attribution:
        mode === 'ai'
          ? `AI-assisted summary, reviewed by ${reviewer} on ${today}.`
          : `Summary written by ${reviewer} on ${today}.`,
    };
  };

  const { valid: recipients, invalid: badRecipients } =
    parseRecipients(recipientsText);
  let emailBlocked = null;
  if (delivery === 'email') {
    if (!mayEmail) {
      emailBlocked =
        'You need the SendImpactReport permission to email reports.';
    } else if (recipients.length === 0)
      emailBlocked = 'Add at least one recipient.';
    else if (badRecipients.length) {
      emailBlocked = `Check these addresses: ${badRecipients.join(', ')}`;
    } else if (recipients.length > MAX_RECIPIENTS) {
      emailBlocked = `Send to at most ${MAX_RECIPIENTS} recipients at a time.`;
    } else if (!subject.trim()) emailBlocked = 'Add a subject.';
  }

  const download = async () => {
    setBuilding(true);
    try {
      const { doc, filename } = await build(buildNarrative());
      doc.save(filename);
      onClose();
    } catch (error) {
      toast.error("We couldn't create the PDF. Please try again.");
    } finally {
      setBuilding(false);
    }
  };

  const send = useMutation(
    async () => {
      const narrative = buildNarrative();
      const { doc, filename } = await build(narrative);
      const form = new FormData();
      form.append(
        'file',
        new Blob([doc.output('arraybuffer')], { type: 'application/pdf' }),
        filename
      );
      form.append('recipients', JSON.stringify(recipients));
      form.append('subject', subject.trim().slice(0, MAX_SUBJECT));
      if (message.trim()) {
        form.append('message', message.trim().slice(0, MAX_MESSAGE));
      }
      form.append('cc_me', String(ccMe));
      if (partner) form.append('partner_id', partner.id);
      form.append(
        'scope',
        JSON.stringify(
          scope.cohortId
            ? { cohort_id: scope.cohortId }
            : { ...(scope.year ? { year: Number(scope.year) } : {}) }
        )
      );
      if (narrative?.attribution) {
        form.append('summary_attribution', narrative.attribution);
      }
      // Sent by the communication service through the gateway.
      return axiosInstancev2.post('/report-emails/sends', form, {
        timeout: 60 * 1000,
      });
    },
    {
      onSuccess: () => {
        toast.success(
          `Report sent to ${recipients.length} recipient${
            recipients.length === 1 ? '' : 's'
          }.`
        );
        setRecipientsText('');
        setMessage('');
        onClose();
      },
    }
  );

  return (
    <Modal
      modalOpen={open}
      title="Download PDF report"
      onClose={() => !building && !send.isLoading && onClose()}
    >
      <div className="space-y-5 text-sm">
        <p className="text-gray-600">
          A branded report for{' '}
          <span className="font-semibold text-gray-900">{scopeLabel}</span>,
          built from the figures on this page.
        </p>

        {issues.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <p className="flex items-center gap-1 font-semibold">
              <WarningAmberIcon sx={{ fontSize: 16 }} /> Check before sharing
            </p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              {issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </div>
        )}

        {!partnersUnavailable && (
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-gray-900">Co-brand with</p>
              <Link
                to="/impact/partners"
                className="text-xs text-claret-500 hover:underline"
              >
                Manage partners
              </Link>
            </div>
            <select
              value={partnerId}
              aria-label="Co-brand with"
              onChange={(event) => setPartnerId(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">No partner (Power Learn Project only)</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.has_logo ? '' : ' (no logo yet)'}
                </option>
              ))}
            </select>
            {partner && (
              <div className="space-y-2 rounded-lg bg-gray-50 p-3">
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={commissioned}
                    onChange={(event) => setCommissioned(event.target.checked)}
                  />
                  {partner.name} commissioned this report
                </label>
                <div
                  className="flex items-center gap-2"
                  aria-label="Logo placement"
                >
                  {(partnerFirst
                    ? [partner.name, 'Power Learn Project']
                    : ['Power Learn Project', partner.name]
                  ).map((label, i) => (
                    <React.Fragment key={label}>
                      {i > 0 && <span className="h-6 w-px bg-gray-300" />}
                      <span className="rounded-md border border-dashed border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700">
                        {label} logo
                      </span>
                    </React.Fragment>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSwapped(!swapped)}
                    className="ml-auto rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:border-gray-400"
                  >
                    Swap sides
                  </button>
                </div>
                <p className="text-[11px] text-gray-500">
                  {partnerFirst
                    ? `${partner.name}'s logo leads on the left.`
                    : "Power Learn Project's logo leads on the left."}{' '}
                  The cover reads &quot;{partner.name} × Power Learn
                  Project&quot;.
                  {!partner.has_logo &&
                    ' This partner has no logo yet, so only their name appears.'}
                </p>
              </div>
            )}
          </section>
        )}

        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-semibold text-gray-900">Summary</p>
            <label className="flex items-center gap-2 text-xs text-gray-600">
              Written for
              <select
                value={audience}
                onChange={(event) => setAudience(event.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
              >
                {AUDIENCES.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {!editing && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={draft.isLoading}
                onClick={() => draft.mutate(false)}
                className="inline-flex items-center gap-1 rounded-md bg-claret-500 px-3 py-1.5 text-sm text-white hover:bg-claret-400 disabled:opacity-60"
              >
                <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                {draft.isLoading
                  ? 'Drafting… (up to a minute)'
                  : 'Draft with AI'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('manual');
                  setText('');
                  setPoints(['', '', '']);
                }}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:border-gray-400"
              >
                Write my own
              </button>
              <span className="self-center text-xs text-gray-500">
                or leave it out
              </span>
            </div>
          )}
          {draft.isError && !editing && (
            <p className="text-xs text-red-700">
              {narrativeError(draft.error)}
            </p>
          )}

          {editing && (
            <div className="space-y-3">
              {mode === 'ai' && (
                <p className="text-xs text-gray-500">
                  Drafted by AI from this report&apos;s totals. Edit anything
                  that doesn&apos;t read right; it will be labelled
                  &quot;AI-assisted, reviewed by {reviewer}&quot;.
                </p>
              )}
              <textarea
                rows={5}
                value={text}
                onChange={(event) => {
                  setText(event.target.value);
                  setReviewed(false);
                }}
                placeholder="A short executive summary…"
                aria-label="Summary"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-claret-500"
              />
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Key points</p>
                {points.map((point, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index} className="flex gap-2">
                    <input
                      value={point}
                      aria-label={`Key point ${index + 1}`}
                      onChange={(event) => {
                        const next = [...points];
                        next[index] = event.target.value;
                        setPoints(next);
                        setReviewed(false);
                      }}
                      className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-claret-500"
                    />
                    <button
                      type="button"
                      aria-label={`Remove key point ${index + 1}`}
                      onClick={() =>
                        setPoints(points.filter((_, i) => i !== index))
                      }
                      className="px-2 text-gray-400 hover:text-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {points.length < 5 && (
                  <button
                    type="button"
                    onClick={() => setPoints([...points, ''])}
                    className="text-xs text-claret-500 hover:underline"
                  >
                    + Add a point
                  </button>
                )}
              </div>
              {caveats.length > 0 && (
                <div className="rounded-md bg-gray-50 p-2 text-xs text-gray-600">
                  <p className="font-medium">Notes printed with the summary:</p>
                  <ul className="list-disc pl-4">
                    {caveats.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
              {facts.length > 0 && (
                <details className="text-xs text-gray-600">
                  <summary className="cursor-pointer">
                    Figures the AI could use ({facts.length})
                  </summary>
                  <ul className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {facts.map((fact) => (
                      <li key={fact.label}>
                        {fact.label}:{' '}
                        <span className="font-semibold">{fact.value}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {flagged.length > 0 && (
                <p className="text-xs text-amber-800">
                  Not found in the report: {flagged.join(', ')}. Check these
                  figures.
                </p>
              )}
              {needsReview && (
                <label className="flex items-start gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={reviewed}
                    onChange={(event) => setReviewed(event.target.checked)}
                    className="mt-0.5"
                  />
                  I&apos;ve read this summary and it is accurate.
                </label>
              )}
              <div className="flex gap-3 text-xs">
                {mode === 'ai' && (
                  <button
                    type="button"
                    disabled={draft.isLoading}
                    onClick={() => draft.mutate(true)}
                    className="text-claret-500 hover:underline disabled:opacity-50"
                  >
                    {draft.isLoading ? 'Drafting…' : 'Draft again'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMode('none');
                    setText('');
                    setPoints([]);
                    setCaveats([]);
                    setFacts([]);
                  }}
                  className="text-gray-500 hover:underline"
                >
                  Remove summary
                </button>
              </div>
              {draft.isError && editing && (
                <p className="text-xs text-red-700">
                  {narrativeError(draft.error)}
                </p>
              )}
            </div>
          )}
        </section>

        <section className="space-y-3 border-t border-gray-100 pt-4">
          <div
            className="inline-flex rounded-lg border border-gray-200 p-1"
            role="tablist"
            aria-label="Delivery"
          >
            {[
              ['download', 'Download'],
              ['email', 'Email to partners'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={delivery === key}
                onClick={() => setDelivery(key)}
                className={`px-3 py-1 rounded-md text-sm ${
                  delivery === key
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {delivery === 'email' && !mayEmail && (
            <p className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
              Emailing reports to partners needs the SendImpactReport
              permission. Ask a SuperAdmin to grant it to your role in Roles
              &amp; Permissions.
            </p>
          )}
          {delivery === 'email' && mayEmail && (
            <div className="space-y-3">
              <label className="block space-y-1">
                <span className="text-xs font-medium text-gray-700">
                  Recipients
                </span>
                <textarea
                  rows={3}
                  value={recipientsText}
                  onChange={(event) => setRecipientsText(event.target.value)}
                  placeholder="partner@example.org, another@example.org"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-claret-500"
                />
                <span className="block text-xs text-gray-500">
                  {recipients.length} recipient
                  {recipients.length === 1 ? '' : 's'} · each gets their own
                  email, so addresses aren&apos;t shared · up to{' '}
                  {MAX_RECIPIENTS}
                </span>
              </label>
              <label className="block space-y-1">
                <span className="text-xs font-medium text-gray-700">
                  Subject
                </span>
                <input
                  value={subject}
                  maxLength={MAX_SUBJECT}
                  onChange={(event) => {
                    setSubject(event.target.value);
                    setSubjectEdited(true);
                  }}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-claret-500"
                />
              </label>
              <label className="block space-y-1">
                <span className="text-xs font-medium text-gray-700">
                  Message (optional)
                </span>
                <textarea
                  rows={4}
                  value={message}
                  maxLength={MAX_MESSAGE}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder={`Please find attached our impact report for ${period}.`}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-claret-500"
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={ccMe}
                  onChange={(event) => setCcMe(event.target.checked)}
                />
                Send me a copy{myEmail ? ` (${myEmail})` : ''}
              </label>
              <p className="text-xs text-gray-500">
                Sent from Power Learn Project with the PDF attached. Replies go
                to {myEmail || 'your email address'}.
              </p>
              {emailBlocked && recipientsText.trim() && (
                <p className="text-xs text-amber-800">{emailBlocked}</p>
              )}
              {send.isError && (
                <p className="text-xs text-red-700">{emailError(send.error)}</p>
              )}
            </div>
          )}
        </section>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            disabled={building || send.isLoading}
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          {delivery === 'download' ? (
            <button
              type="button"
              disabled={!canDownload}
              onClick={download}
              className="rounded-md bg-claret-500 px-4 py-2 text-sm text-white hover:bg-claret-400 disabled:opacity-40"
            >
              {building ? 'Creating PDF…' : 'Download PDF'}
            </button>
          ) : (
            <button
              type="button"
              disabled={!canDownload || Boolean(emailBlocked) || send.isLoading}
              onClick={() => send.mutate()}
              className="rounded-md bg-claret-500 px-4 py-2 text-sm text-white hover:bg-claret-400 disabled:opacity-40"
            >
              {(() => {
                if (send.isLoading) return 'Sending…';
                if (!recipients.length) return 'Send';
                return `Send to ${recipients.length} recipient${
                  recipients.length === 1 ? '' : 's'
                }`;
              })()}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ImpactPdfDialog;
